import React, { useState, useEffect } from 'react';
import { FormGroup, Label, Input, Button } from 'reactstrap';
import styled from 'styled-components';

const FormBox = styled.div`
  border: 2px solid #6610f2;
  border-radius: 8px;
  padding: 20px;
  background: linear-gradient(135deg, #e7e3ff, #ffffff);
  margin-bottom: 20px;

  h5 {
    color: #6610f2;
    margin-bottom: 15px;
    font-weight: 600;
  }
`;

const HelpText = styled.div`
  background: #d1ecf1;
  border-left: 4px solid #17a2b8;
  padding: 10px;
  margin-bottom: 15px;
  border-radius: 4px;
  font-size: 13px;
  color: #0c5460;
`;

const QuestionBlock = styled.div`
  border: 2px solid #e0e0e0;
  border-radius: 6px;
  padding: 15px;
  margin-bottom: 15px;
  background: white;
  position: relative;
`;

const QuestionNumber = styled.div`
  position: absolute;
  top: -12px;
  left: 12px;
  background: #6610f2;
  color: white;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: bold;
`;

const OptionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
`;

const OptionBadge = styled.span`
  background: #6610f2;
  color: white;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 14px;
  flex-shrink: 0;
`;

const RadioLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  font-size: 14px;
  margin: 0;
`;

interface FillBlanksMCQFormProps {
  formData: any;
  onChange: (data: any) => void;
}

const FillBlanksMCQForm: React.FC<FillBlanksMCQFormProps> = ({ formData, onChange }) => {
  const parseContent = () => {
    try {
      return typeof formData.content === 'string' && formData.content
        ? JSON.parse(formData.content)
        : {
            passage: '',
            questions: [],
          };
    } catch {
      return {
        passage: '',
        questions: [],
      };
    }
  };

  const [contentData, setContentData] = useState(parseContent());

  useEffect(() => {
    onChange({
      ...formData,
      content: JSON.stringify(contentData),
    });
  }, [contentData]);

  const handlePassageChange = (value: string) => {
    setContentData({
      ...contentData,
      passage: value,
    });
  };

  const handleAddQuestion = () => {
    setContentData({
      ...contentData,
      questions: [
        ...contentData.questions,
        {
          options: ['', '', '', ''],
          correct_index: 0,
        },
      ],
    });
  };

  const handleRemoveQuestion = (qIndex: number) => {
    const newQuestions = contentData.questions.filter((_: any, i: number) => i !== qIndex);
    setContentData({
      ...contentData,
      questions: newQuestions,
    });
  };

  const handleOptionChange = (qIndex: number, optIndex: number, value: string) => {
    const newQuestions = [...contentData.questions];
    newQuestions[qIndex].options[optIndex] = value;
    setContentData({
      ...contentData,
      questions: newQuestions,
    });
  };

  const handleCorrectChange = (qIndex: number, correctIndex: number) => {
    const newQuestions = [...contentData.questions];
    newQuestions[qIndex].correct_index = correctIndex;
    setContentData({
      ...contentData,
      questions: newQuestions,
    });
  };

  const optionLabels = ['A', 'B', 'C', 'D'];

  return (
    <FormBox>
      <h5>📝 Nội dung câu hỏi - Điền từ (Trắc nghiệm ABCD)</h5>
      
      <HelpText>
        💡 <strong>Hướng dẫn:</strong> Dùng <code>___</code> để đánh dấu chỗ trống trong đoạn văn.
        Mỗi chỗ trống sẽ có 4 lựa chọn ABCD.
        <br/>
        Ví dụ: "Lan ___ to school. She ___ happy."
      </HelpText>

      <FormGroup>
        <Label>Đoạn văn có chỗ trống: *</Label>
        <Input
          type="textarea"
          value={contentData.passage || ''}
          onChange={(e) => handlePassageChange(e.target.value)}
          placeholder="Nhập đoạn văn và dùng ___ để đánh dấu chỗ trống...

Ví dụ: Yesterday, I ___ to the park. It ___ very beautiful."
          rows={4}
        />
        <small className="form-text text-muted">
          Dùng <code>___</code> để đánh dấu mỗi chỗ trống
        </small>
      </FormGroup>

      <FormGroup>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '10px'
        }}>
          <Label style={{ margin: 0 }}>
            Câu hỏi cho từng chỗ trống: * ({contentData.questions.length} câu)
          </Label>
          <Button color="primary" size="sm" onClick={handleAddQuestion}>
            + Thêm câu hỏi
          </Button>
        </div>

        {contentData.questions.length === 0 ? (
          <div style={{ 
            padding: '30px', 
            textAlign: 'center', 
            background: '#f9f9f9',
            borderRadius: '6px',
            color: '#999'
          }}>
            Chưa có câu hỏi nào. Nhấn "Thêm câu hỏi" để bắt đầu.
          </div>
        ) : (
          contentData.questions.map((q: any, qIndex: number) => (
            <QuestionBlock key={qIndex}>
              <QuestionNumber>Chỗ trống #{qIndex + 1}</QuestionNumber>
              
              <div style={{ marginBottom: '12px' }}>
                {optionLabels.map((label, optIndex) => (
                  <OptionRow key={optIndex}>
                    <OptionBadge>{label}</OptionBadge>
                    <Input
                      type="text"
                      value={q.options[optIndex] || ''}
                      onChange={(e) => handleOptionChange(qIndex, optIndex, e.target.value)}
                      placeholder={`Lựa chọn ${label}...`}
                      style={{ flex: 1 }}
                    />
                    <RadioLabel>
                      <input
                        type="radio"
                        name={`correct_q${qIndex}`}
                        checked={q.correct_index === optIndex}
                        onChange={() => handleCorrectChange(qIndex, optIndex)}
                      />
                      <span style={{ color: q.correct_index === optIndex ? '#28a745' : '#999' }}>
                        Đúng
                      </span>
                    </RadioLabel>
                  </OptionRow>
                ))}
              </div>

              <Button
                color="danger"
                size="sm"
                onClick={() => handleRemoveQuestion(qIndex)}
                style={{ fontSize: '12px' }}
              >
                🗑️ Xóa câu hỏi này
              </Button>
            </QuestionBlock>
          ))
        )}
      </FormGroup>

      {/* Preview */}
      <div style={{ 
        background: '#e7e3ff', 
        padding: '15px', 
        borderRadius: '4px',
        marginTop: '15px'
      }}>
        <strong style={{ fontSize: '13px', color: '#4527a0' }}>📋 Xem trước:</strong>
        {contentData.passage && (
          <div style={{ 
            marginTop: '10px',
            padding: '12px',
            background: 'white',
            borderRadius: '4px',
            whiteSpace: 'pre-wrap',
            marginBottom: '12px'
          }}>
            {contentData.passage}
          </div>
        )}
        {contentData.questions.length > 0 && (
          <div>
            {contentData.questions.map((q: any, qIndex: number) => (
              <div key={qIndex} style={{ 
                background: 'white',
                padding: '12px',
                borderRadius: '4px',
                marginBottom: '8px'
              }}>
                <div style={{ 
                  fontWeight: 'bold', 
                  marginBottom: '8px',
                  color: '#6610f2'
                }}>
                  Chỗ trống #{qIndex + 1}:
                </div>
                {q.options.map((opt: string, optIndex: number) => (
                  <div key={optIndex} style={{ 
                    fontSize: '13px',
                    marginBottom: '4px',
                    color: q.correct_index === optIndex ? '#28a745' : '#333',
                    fontWeight: q.correct_index === optIndex ? 'bold' : 'normal'
                  }}>
                    {optionLabels[optIndex]}. {opt || '(chưa điền)'}
                    {q.correct_index === optIndex && ' ✓'}
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Debug JSON */}
      <details style={{ marginTop: '15px' }}>
        <summary style={{ cursor: 'pointer', color: '#666', fontSize: '14px' }}>
          🔍 Xem JSON (Debug)
        </summary>
        <pre style={{ 
          background: '#f5f5f5', 
          padding: '10px', 
          borderRadius: '4px',
          fontSize: '12px',
          maxHeight: '200px',
          overflow: 'auto',
          marginTop: '10px'
        }}>
          {JSON.stringify(contentData, null, 2)}
        </pre>
      </details>
    </FormBox>
  );
};

export default FillBlanksMCQForm;
